import { Close, Modal } from '../../../../modules/design-system';
import { activeOddsLevel, MOD_LABELS, type IframePoolOfferOdds, type VolatilityLevel } from '../../../../modules/api';
import { ProvablyFairSection } from '../../../../modules/fairness/ui';
import { OddsBarsList } from '../OddsBar';
import { BREAKDOWN_CARD_STYLE } from '../OddsBreakdown';
import { StatsSummary, ViewOddsTrigger } from '../OddsPanel';
import { ModLabel } from './ModLabel';
import { VolatilityTabs } from './VolatilityTabs';

const TITLE_ID = 'odds-modal-title';

export interface OddsModalProps {
  odds: IframePoolOfferOdds;
  volatility: VolatilityLevel;
  onVolatilityChange: (level: VolatilityLevel) => void;
  onClose: () => void;
}

export function OddsModal({ odds, volatility, onVolatilityChange, onClose }: OddsModalProps) {
  const level = activeOddsLevel(odds, volatility);
  const modLabel = MOD_LABELS[volatility];
  return (
    <Modal onClose={onClose} inspectName="OddsModal" ariaLabelledBy={TITLE_ID} widthClassName="max-w-[463px]">
      <section
        data-co-component="OddsBreakdown"
        className="flex flex-col gap-3 overflow-hidden p-4"
        // No card `backdrop-filter` on purpose: the Modal backdrop already
        // blurs the page behind this card, and WebKit composites a nested
        // `backdrop-filter` under Modal's animating wrapper only when that
        // animation ends — a native host reported it as a second, late dim
        // snapping onto the panel.
        style={BREAKDOWN_CARD_STYLE}
      >
        {/* Header inside the card — X on the left, title centred (desktop
            Figma 19074:259466). Same surface / text tokens as Apply. */}
        <header className="relative flex h-8 items-center justify-center">
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute left-0 inline-flex h-8 w-8 items-center justify-center rounded-full transition-opacity hover:opacity-90 active:scale-95"
            style={{
              color: 'var(--co-OddsBreakdown-modValueColor, #ffffff)',
              backgroundColor: 'var(--co-OddsBreakdown-barTrack, rgba(255,255,255,0.08))',
            }}
          >
            <Close size={16} strokeWidth={2} />
          </button>
          <h2
            id={TITLE_ID}
            className="m-0 text-[18px] leading-[24px] font-semibold"
            style={{ color: 'var(--co-OddsBreakdown-modValueColor, #ffffff)' }}
          >
            Odds
          </h2>
        </header>
        <VolatilityTabs value={volatility} onChange={onVolatilityChange} />
        <div data-co-component="StatsRow">
          <StatsSummary
            minValue={level.minValue}
            maxPull={level.maxValue}
            rightSlot={<ViewOddsTrigger modLabel={modLabel} />}
          />
        </div>
        <OddsBarsList tiers={level.tiers} />
        <ModLabel modLabel={modLabel} />
        <ProvablyFairSection offerId={odds.id} skin="modern" />
        <button
          type="button"
          onClick={onClose}
          className="mt-1 w-full rounded-full py-3 text-[15px] font-semibold"
          style={{
            color: 'var(--co-OddsBreakdown-modValueColor, #ffffff)',
            backgroundColor: 'var(--co-OddsBreakdown-barTrack, rgba(255,255,255,0.08))',
          }}
        >
          Apply
        </button>
      </section>
    </Modal>
  );
}
